import { BaseAdapter } from "./base-adapter";
import type { TEvent } from "../types";
import { EventType } from "../types";

/**
 * Configuration interface for KwikPass Adapter
 */
export interface KwikPassConfig {
  enableDebugLogs?: boolean;
  enableKwikPassEvents?: boolean;
}

/**
 * KwikPass Analytics Adapter for tracking events to KwikPass platform
 */
export class KwikPassAdapter extends BaseAdapter {
  public readonly name = "KwikPass";
  private enableKwikPassEvents: boolean;

  constructor(config: KwikPassConfig = {}) {
    super(config);
    this.enableKwikPassEvents = config.enableKwikPassEvents ?? true;
    // Initialize logger with proper adapter name
    this.initializeLogger();
  }

  /**
   * Initialize the KwikPass adapter
   */
  public async initialize(): Promise<void> {
    try {
      if (typeof window !== "undefined") {
        this.logger.info("KwikPass adapter initialized successfully", {
          eventsEnabled: this.enableKwikPassEvents,
        });
      }
      this.initialized = true;
    } catch (error) {
      this.logger.error(
        "Failed to initialize KwikPass adapter",
        error as Error
      );
      throw error;
    }
  }

  /**
   * Track an event using KwikPass
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined") {
      this.logger.warn(
        "KwikPass adapter not initialized or not in browser environment"
      );
      return;
    }

    if (!this.enableKwikPassEvents) {
      this.logger.debug("KwikPass events disabled, skipping event", {
        eventType: event.type,
      });
      return;
    }

    try {
      this.logger.debug("Processing event", { eventType: event.type });

      // Enhanced event data with affiliate parameters
      const enhancedEvent = this.enhanceWithAffiliateParams(event);

      switch (event.type) {
        case EventType.PRODUCT_VIEW:
          this.trackProductView(enhancedEvent);
          break;

        case EventType.COLLECTION_VIEW:
          this.trackCollectionView(enhancedEvent);
          break;

        case EventType.PAGE_VIEW:
          this.trackPageView(enhancedEvent);
          break;

        default:
          this.logger.debug("Event not supported", { eventType: event.type });
          break;
      }
    } catch (error) {
      this.logger.error("Error tracking KwikPass event", error as Error, {
        eventType: event.type,
      });
    }
  }

  /**
   * Send event to KwikPass platform
   */
  private sendKwikPassEvent(type: string, eventData: any): void {
    this.logger.debug(`Sending ${type}`, { eventData });

    // Dispatch custom event for KwikPass to listen
    if (typeof window !== "undefined") {
      const customEvent = new CustomEvent(`page_view_kp`, {
        detail: {
          type,
          detail: eventData,
        },
      });
      window.dispatchEvent(customEvent);
      this.logger.debug(`${type} sent successfully`);
    }
  }

  /**
   * Track product view event
   */
  private trackCollectionView(event: any): void {
    this.sendKwikPassEvent("collection", {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency,
      category: event.category,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track collection view event
   */
  private trackProductView(event: any): void {
    this.sendKwikPassEvent("product", {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency,
      category: event.category,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track page view event
   */
  private trackPageView(event: any): void {
    this.sendKwikPassEvent("other", {
      path: event.path,
      title: event.title,
      referrer: event.referrer,
      timestamp: event.timestamp || Date.now(),
    });
  }
}
